import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  serverTimestamp,
  DocumentReference
} from 'firebase/firestore';
import { db } from '@lib/firebase';

import { ValidationError } from '@/shared/api/errors/ApiError';
import { getCreatedTimestamp } from '@/shared/utils/api/timestampHelper';
import ServiceBase from '@/shared/api/base/ServiceBase';
import APP_CONFIG from '@/shared/constants/app/appConfig';
import loggingService from '@/shared/services/loggingService';

const PVQ_COLLECTION = 'pvqQuestions';
const VERIFICATION_COLLECTION = 'identityVerifications';

export interface PVQQuestion {
  id: string;
  question: string;
  category: string;
  active: boolean;
  options?: string[];
  questionNumber?: number;
  questionKey?: string;
}

export interface PVQVerification {
  id: string;
  userId: string;
  courseId: string;
  sessionId: string;
  questionId: string;
  question: string;
  answer: string;
  isCorrect: boolean;
  timeToAnswer: number;
  ipAddress: string;
  deviceInfo?: any;
  timestamp: any;
  [key: string]: any;
}

export interface VerificationResult {
  valid: boolean;
  message: string;
}

export interface LockoutStatus {
  isLockedOut: boolean;
  remainingSeconds: number;
  lastVerificationTime?: Date;
  message: string;
}

const defaultPVQQuestions: PVQQuestion[] = [
  {
    id: 'pvq_001',
    question: 'What is your mother\'s maiden name?',
    category: 'personal',
    active: true
  },
  {
    id: 'pvq_002',
    question: 'In what city were you born?',
    category: 'personal',
    active: true
  },
  {
    id: 'pvq_003',
    question: 'What was the name of your first pet?',
    category: 'personal',
    active: true
  },
  {
    id: 'pvq_004',
    question: 'What is your favorite color?',
    category: 'general',
    active: true
  },
  {
    id: 'pvq_005',
    question: 'In what year did you graduate high school?',
    category: 'personal',
    active: true
  },
  {
    id: 'pvq_006',
    question: 'What is the name of your elementary school?',
    category: 'personal',
    active: true
  },
  {
    id: 'pvq_007',
    question: 'Who is your favorite musician or band?',
    category: 'general',
    active: true
  },
  {
    id: 'pvq_008',
    question: 'What is your favorite book or movie?',
    category: 'general',
    active: true
  },
  {
    id: 'pvq_009',
    question: 'What is the street name of the street you grew up on?',
    category: 'personal',
    active: true
  },
  {
    id: 'pvq_010',
    question: 'In what state did you live as a child?',
    category: 'personal',
    active: true
  }
];

class PVQService extends ServiceBase {
  constructor() {
    super('PVQService');
  }

  /**
   * Get all active PVQ questions
   * @returns {Promise<PVQQuestion[]>} List of questions
   */
  async getPVQQuestions(): Promise<PVQQuestion[]> {
    try {
      // E2E Test Mock Injection
      if (typeof window !== 'undefined' && (window as any).MOCK_SECURITY_QUESTION) {
        this.log('Returning MOCKED PVQ Questions for E2E testing');
        return [
          {
            id: 'pvq-1',
            question: (window as any).MOCK_SECURITY_QUESTION.question,
            category: 'personal',
            active: true
          }
        ];
      }

      const questionsRef = collection(db, PVQ_COLLECTION);
      const q = query(questionsRef, where('active', '==', true));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return defaultPVQQuestions;
      }

      const questions: PVQQuestion[] = [];
      snapshot.forEach(doc => {
        questions.push({
          id: doc.id,
          ...doc.data()
        } as PVQQuestion);
      });

      return questions;
    } catch (error) {
      this.logError(error, { method: 'getPVQQuestions' });
      throw error;
    }
  }

  /**
   * Get a random active PVQ question
   * @returns {Promise<PVQQuestion | null>} Random question
   */
  async getRandomPVQQuestion(): Promise<PVQQuestion | null> {
    try {
      const questions = await this.getPVQQuestions();
      if (questions.length === 0) {
        return null;
      }
      const randomIndex = Math.floor(Math.random() * questions.length);
      return questions[randomIndex];
    } catch (error) {
      this.logError(error, { method: 'getRandomPVQQuestion' });
      throw error;
    }
  }

  /**
   * Log an identity verification attempt
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @param {string} sessionId Session ID
   * @param {Object} pvqData PVQ data (questionId, question, answer, isCorrect, etc.)
   * @returns {Promise<string>} Verification document ID
   */
  async logIdentityVerification(userId: string, courseId: string, sessionId: string, pvqData: any): Promise<string> {
    try {
      // E2E Test Mock Injection
      if (typeof window !== 'undefined' && (window as any).MOCK_SESSION_ID) {
        this.log('Returning MOCKED logIdentityVerification for E2E testing');
        return 'mock-verification-id';
      }

      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      this.validate.validatePVQData({ ...pvqData, userId });
      if (!sessionId || typeof sessionId !== 'string') {
        throw new ValidationError('Session ID is required');
      }

      const verificationRef = collection(db, 'users', userId, VERIFICATION_COLLECTION);
      const docRef = await addDoc(verificationRef, {
        userId,
        courseId,
        sessionId,
        questionId: pvqData.questionId,
        question: pvqData.question,
        answer: pvqData.answer,
        isCorrect: pvqData.isCorrect,
        timeToAnswer: pvqData.timeToAnswer,
        ipAddress: pvqData.ipAddress,
        deviceInfo: pvqData.deviceInfo || null,
        timestamp: serverTimestamp(),
        ...getCreatedTimestamp()
      });

      return docRef.id;
    } catch (error) {
      this.logError(error, { method: 'logIdentityVerification', userId, courseId, sessionId });
      throw error;
    }
  }

  /**
   * Get verification history for a user and course
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @param {number} limitCount Max number of records
   * @returns {Promise<PVQVerification[]>} List of verifications
   */
  async getVerificationHistory(userId: string, courseId: string, limitCount = 50): Promise<PVQVerification[]> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      if (typeof limitCount !== 'number' || limitCount <= 0) {
        throw new ValidationError('Limit must be a positive number');
      }

      const verificationRef = collection(db, 'users', userId, VERIFICATION_COLLECTION);
      const q = query(
        verificationRef,
        where('userId', '==', userId),
        where('courseId', '==', courseId)
      );

      const snapshot = await getDocs(q);
      const verifications: PVQVerification[] = [];

      snapshot.forEach(doc => {
        verifications.push({
          id: doc.id,
          ...doc.data()
        } as PVQVerification);
      });

      return verifications.slice(-limitCount);
    } catch (error) {
      this.logError(error, { method: 'getVerificationHistory', userId, courseId, limitCount });
      throw error;
    }
  }

  /**
   * Check if a user has already answered a specific PVQ
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @param {string} questionId Question ID
   * @returns {Promise<boolean>} True if answered
   */
  async getUserAnsweredPVQ(userId: string, courseId: string, questionId: string): Promise<boolean> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      if (!questionId || typeof questionId !== 'string') {
        throw new ValidationError('Question ID is required');
      }

      const verificationRef = collection(db, 'users', userId, VERIFICATION_COLLECTION);
      const q = query(
        verificationRef,
        where('userId', '==', userId),
        where('courseId', '==', courseId),
        where('questionId', '==', questionId)
      );

      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch (error) {
      this.logError(error, { method: 'getUserAnsweredPVQ', userId, courseId, questionId });
      throw error;
    }
  }

  /**
   * Validate a PVQ answer against stored verification
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @param {string} questionId Question ID
   * @param {string} userAnswer User provided answer
   * @returns {Promise<VerificationResult>} Validation result
   */
  async validatePVQAnswer(userId: string, courseId: string, questionId: string, userAnswer: string): Promise<VerificationResult> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      if (!questionId || typeof questionId !== 'string') {
        throw new ValidationError('Question ID is required');
      }
      if (!userAnswer || typeof userAnswer !== 'string') {
        throw new ValidationError('User answer is required');
      }

      const verificationRef = collection(db, 'users', userId, VERIFICATION_COLLECTION);
      const q = query(
        verificationRef,
        where('userId', '==', userId),
        where('courseId', '==', courseId),
        where('questionId', '==', questionId)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return { valid: false, message: 'No answer found for this question' };
      }

      const doc = snapshot.docs[0];
      const verificationData = doc.data();

      const normalizedUserAnswer = userAnswer.toLowerCase().trim();
      const normalizedStoredAnswer = verificationData.answer.toLowerCase().trim();

      const isValid = normalizedUserAnswer === normalizedStoredAnswer;

      return {
        valid: isValid,
        message: isValid ? 'Answer verified' : 'Incorrect answer. Please try again.'
      };
    } catch (error) {
      this.logError(error, { method: 'validatePVQAnswer', userId, courseId, questionId });
      throw error;
    }
  }

  /**
   * Record a PVQ attempt
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @param {string} sessionId Session ID
   * @param {Object} attemptData Attempt details
   * @returns {Promise<string>} Attempt document ID
   */
  async recordPVQAttempt(userId: string, courseId: string, sessionId: string, attemptData: any): Promise<string> {
    try {
      this.validate.validatePVQData({ ...attemptData, userId });

      // E2E Test Mock Injection
      const isMocked = typeof window !== 'undefined' &&
        ((window as any).MOCK_SESSION_ID || localStorage.getItem('MOCK_SESSION_ID'));

      if (isMocked) {
        this.log('Returning MOCKED recordPVQAttempt for E2E testing');
        return 'mock-attempt-id';
      }

      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);
      if (!sessionId || typeof sessionId !== 'string') {
        throw new ValidationError('Session ID is required');
      }

      const verificationRef = collection(db, 'users', userId, VERIFICATION_COLLECTION);
      const docRef = await addDoc(verificationRef, {
        userId,
        courseId,
        sessionId,
        questionId: attemptData.questionId,
        question: attemptData.question,
        answer: attemptData.answer,
        isCorrect: attemptData.isCorrect,
        timeToAnswer: attemptData.timeToAnswer,
        ipAddress: attemptData.ipAddress,
        deviceInfo: attemptData.deviceInfo || null,
        attemptTimestamp: serverTimestamp(),
        ...getCreatedTimestamp()
      });

      // Audit log the verification attempt
      loggingService.logAuditEvent(
        userId,
        attemptData.isCorrect ? 'IDENTITY_VERIFIED' : 'IDENTITY_VERIFICATION_FAILED',
        'pvq',
        docRef.id,
        attemptData.isCorrect ? 'success' : 'failure',
        {
          sessionId,
          questionId: attemptData.questionId,
          timeToAnswer: attemptData.timeToAnswer
        }
      );

      return docRef.id;
    } catch (error) {
      this.logError(error, { method: 'recordPVQAttempt', userId, courseId, sessionId });
      throw error;
    }
  }

  /**
   * Check if user is locked out from PVQ due to 24h rule
   * @param {string} userId User ID
   * @param {string} courseId Course ID
   * @returns {Promise<LockoutStatus>} Lockout status
   */
  async checkPVQLockout(userId: string, courseId: string): Promise<LockoutStatus> {
    try {
      this.validate.validateUserId(userId);
      this.validate.validateCourseId(courseId);

      // E2E Test Mock Injection
      const rawMock = typeof window !== 'undefined' ? (localStorage.getItem('MOCK_PVQ_LOCKOUT') || (window as any).MOCK_PVQ_LOCKOUT) : undefined;
      if (rawMock) {
        this.log('Returning MOCKED PVQ Lockout for E2E testing');
        return typeof rawMock === 'string' ? JSON.parse(rawMock) : rawMock;
      }

      // Instead of relying on local cached identityVerifications, which can be out of sync,
      // read the user document directly as the single source of truth for lockout status.
      const userRef = collection(db, 'users');
      const q = query(userRef, where('__name__', '==', userId));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return {
          isLockedOut: false,
          remainingSeconds: 0,
          message: 'Ready for identity verification'
        };
      }

      const userData = snapshot.docs[0].data();
      const pvqLockoutUntil = userData.pvqLockoutUntil;

      if (!pvqLockoutUntil) {
        return {
          isLockedOut: false,
          remainingSeconds: 0,
          message: 'Ready for identity verification'
        };
      }

      const lockoutTime = pvqLockoutUntil?.toDate?.() || new Date(pvqLockoutUntil);
      const now = new Date();
      
      const secondsElapsed = Math.floor((now.getTime() - lockoutTime.getTime()) / 1000);
      const remainingSeconds = Math.max(0, -secondsElapsed); // secondsElapsed is negative if in future

      if (remainingSeconds > 0) {
        return {
          isLockedOut: true,
          remainingSeconds,
          lastVerificationTime: userData.pvqLockedOutAt?.toDate?.() || now,
          message: `Identity verification locked due to failed attempts. Please try again in ${Math.ceil(remainingSeconds / 60)} minute(s).`
        };
      }

      return {
        isLockedOut: false,
        remainingSeconds: 0,
        message: 'Ready for identity verification'
      };
    } catch (error) {
      this.logError(error, { method: 'checkPVQLockout', userId, courseId });
      throw error;
    }
  }
}

const pvqServices = new PVQService();
export default pvqServices;

// Maintain backward compatibility for named exports
export const getPVQQuestions = () => pvqServices.getPVQQuestions();
export const getRandomPVQQuestion = () => pvqServices.getRandomPVQQuestion();
export const logIdentityVerification = (uId: string, cId: string, sId: string, data: any) => pvqServices.logIdentityVerification(uId, cId, sId, data);
export const getVerificationHistory = (uId: string, cId: string, limit?: number) => pvqServices.getVerificationHistory(uId, cId, limit);
export const getUserAnsweredPVQ = (uId: string, cId: string, qId: string) => pvqServices.getUserAnsweredPVQ(uId, cId, qId);
export const validatePVQAnswer = (uId: string, cId: string, qId: string, ans: string) => pvqServices.validatePVQAnswer(uId, cId, qId, ans);
export const recordPVQAttempt = (uId: string, cId: string, sId: string, data: any) => pvqServices.recordPVQAttempt(uId, cId, sId, data);
export const checkPVQLockout = (uId: string, cId: string) => pvqServices.checkPVQLockout(uId, cId);
